import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Filter, ChevronDown, Heart, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = "https://seedoza-backend1.onrender.com";
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [wishlistLoading, setWishlistLoading] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const { isAuthenticated, token } = useAuth();
  
  const [filters, setFilters] = useState({
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    sort_by: searchParams.get('sort_by') || '',
    in_stock: searchParams.get('in_stock') || ''
  });

  useEffect(() => {
    fetchProducts();
    if (isAuthenticated()) {
      fetchWishlist();
    }
  }, [searchParams]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      if (searchParams.get('min_price')) params.append('min_price', searchParams.get('min_price'));
      if (searchParams.get('max_price')) params.append('max_price', searchParams.get('max_price'));
      if (searchParams.get('sort_by')) params.append('sort_by', searchParams.get('sort_by'));
      if (searchParams.get('in_stock')) params.append('in_stock', searchParams.get('in_stock'));
      
      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlistIds(new Set(response.data.map(item => item.product_id)));
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  };

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/products/search/autocomplete?q=${encodeURIComponent(query)}`);
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        fetchSuggestions(searchQuery);
      } else {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, fetchSuggestions]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery) {
      newParams.set('search', searchQuery);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.name);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('search', suggestion.name);
    setSearchParams(newParams);
    setShowSuggestions(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    
    setSearchParams(newParams);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ min_price: '', max_price: '', sort_by: '', in_stock: '' });
    setSearchQuery('');
    setSearchParams(new URLSearchParams());
  };

  const toggleWishlist = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated()) {
      toast.error('Please login to add to wishlist');
      return;
    }

    setWishlistLoading(prev => ({ ...prev, [productId]: true }));
    
    try {
      if (wishlistIds.has(productId)) {
        await axios.delete(`${API}/wishlist/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlistIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist`, { product_id: productId }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWishlistIds(prev => new Set([...prev, productId]));
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    } finally {
      setWishlistLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const hasActiveFilters = searchParams.get('search') || searchParams.get('min_price') || 
    searchParams.get('max_price') || searchParams.get('sort_by') || searchParams.get('in_stock');

  return (
    <div className="section-spacing" data-testid="products-page">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4" data-testid="products-heading">
            Our Products
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Discover our range of healthy energy snacks made from powerful seeds
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white rounded-2xl p-6 shadow-card mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  data-testid="search-input"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  className="pl-10"
                />
              </div>
              
              {/* Autocomplete Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-20 w-full mt-1 bg-white rounded-lg shadow-lg border border-border overflow-hidden">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors text-left"
                    >
                      {suggestion.image && (
                        <img 
                          src={suggestion.image} 
                          alt={suggestion.name} 
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      )}
                      <span>{suggestion.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </form>

            {/* Filter Toggle */}
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
              data-testid="filter-toggle"
            >
              <Filter className="w-4 h-4" />
              Filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium mb-2">Min Price</label>
                  <Input
                    type="number"
                    placeholder="₹0"
                    value={filters.min_price}
                    onChange={(e) => handleFilterChange('min_price', e.target.value)}
                    data-testid="min-price-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Max Price</label>
                  <Input
                    type="number"
                    placeholder="₹999"
                    value={filters.max_price}
                    onChange={(e) => handleFilterChange('max_price', e.target.value)}
                    data-testid="max-price-input"
                  />
                </div>
                
                {/* Sort */}
                <div>
                  <label className="block text-sm font-medium mb-2">Sort By</label>
                  <select
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    data-testid="sort-select"
                  >
                    <option value="">Default</option>
                    <option value="price_asc">Price: Low to High</option>
                    <option value="price_desc">Price: High to Low</option>
                    <option value="name_asc">Name: A-Z</option>
                    <option value="name_desc">Name: Z-A</option>
                    <option value="newest">Newest First</option>
                  </select>
                </div>
                
                {/* Stock Filter */}
                <div>
                  <label className="block text-sm font-medium mb-2">Availability</label>
                  <select
                    value={filters.in_stock}
                    onChange={(e) => handleFilterChange('in_stock', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    data-testid="stock-select"
                  >
                    <option value="">All Products</option>
                    <option value="true">In Stock Only</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-4 mt-4">
                <Button onClick={applyFilters} className="btn-primary" data-testid="apply-filters-btn">
                  Apply Filters
                </Button>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                    <X className="w-4 h-4 mr-2" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 mb-6">
            {searchParams.get('search') && (
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm flex items-center gap-2">
                Search: {searchParams.get('search')}
                <button onClick={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('search');
                  setSearchParams(newParams);
                  setSearchQuery('');
                }}>
                  <X className="w-4 h-4" />
                </button>
              </span>
            )}
            {searchParams.get('sort_by') && (
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                Sort: {searchParams.get('sort_by').replace('_', ' ')}
              </span>
            )}
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 mx-auto text-primary animate-spin mb-4" />
            <p className="text-lg text-muted-foreground">Loading products...</p>
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <div key={product.id} className="product-card group relative" data-testid={`product-${product.id}`}>
                {/* Wishlist Button */}
                <button
                  onClick={(e) => toggleWishlist(product.id, e)}
                  disabled={wishlistLoading[product.id]}
                  className={`absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    wishlistIds.has(product.id) 
                      ? 'bg-red-500 text-white' 
                      : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
                  }`}
                  data-testid={`wishlist-btn-${product.id}`}
                >
                  {wishlistLoading[product.id] ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Heart className={`w-5 h-5 ${wishlistIds.has(product.id) ? 'fill-current' : ''}`} />
                  )}
                </button>
                
                <Link to={`/products/${product.id}`}>
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 space-y-4">
                    <h3 className="text-2xl font-heading font-semibold">{product.name}</h3>
                    <p className="text-muted-foreground line-clamp-2">{product.description}</p>
                    
                    {/* Variants */}
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Available sizes:</p>
                      <div className="flex flex-wrap gap-2">
                        {product.variants.map((variant, idx) => (
                          <span key={idx} className="text-sm bg-muted px-3 py-1 rounded-full">
                            {variant.size} - ₹{variant.price}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="px-6 pb-6">
                  <Link to={`/products/${product.id}`}>
                    <button className="w-full btn-primary" data-testid={`view-details-${product.id}`}>
                      View Details
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl shadow-card">
            <Search className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">No products found</h2>
            <p className="text-muted-foreground mb-6">
              Try adjusting your search or filters to find what you're looking for
            </p>
            <Button onClick={clearFilters} className="btn-primary">
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;
